import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import DictIconBadge from './DictIconBadge';
import DictProgress from './DictProgress';
import { resolveCustomIcon } from './customIcons';
import { CustomDictionaryMock } from '../mockCustomDictionaries';
import { relDayLabel } from '../utils';

interface ICustomDictionaryCardProps {
  dict: CustomDictionaryMock;
}

const CustomDictionaryCard: React.FC<ICustomDictionaryCardProps> = ({
  dict,
}) => {
  const [hover, setHover] = useState(false);
  const Icon = resolveCustomIcon(dict.icon);

  // Custom dictionaries are mocked; clicking the card (or its CTA) is a no-op
  // for now. Cursor + hover still hint the block is interactive so the muscle
  // memory carries over once the BE wires real custom-dict routes.
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: hover ? dict.accent : 'primary.tableBorder',
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        p: '18px 18px 16px',
        transition: 'border-color .2s ease',
      }}
    >
      <Stack direction={'row'} alignItems={'flex-start'} spacing={1.5}>
        <DictIconBadge Icon={Icon} accent={dict.accent} size={42} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component={'h3'}
            sx={{
              fontSize: 18,
              fontWeight: 700,
              m: 0,
              mt: 0.25,
              mb: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {dict.name}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {'оновлено '}
            {relDayLabel(dict.lastTouched)}
          </Typography>
        </Box>
        <Stack
          direction={'row'}
          spacing={0.25}
          sx={{
            opacity: hover ? 1 : 0,
            transition: 'opacity .15s ease',
          }}
        >
          <IconButton
            aria-label={'Перейменувати'}
            title={'Перейменувати'}
            onClick={(e) => e.stopPropagation()}
            sx={(theme) => ({
              borderRadius: 1,
              color: 'text.secondary',
              height: 34,
              width: 34,
              '&:hover': {
                bgcolor:
                  theme.palette.mode === 'light'
                    ? 'rgba(59, 44, 147, 0.06)'
                    : 'rgba(155, 124, 255, 0.10)',
                color: 'primary.main',
              },
            })}
          >
            <EditIcon sx={{ fontSize: 19 }} />
          </IconButton>
          <IconButton
            aria-label={'Видалити'}
            title={'Видалити'}
            onClick={(e) => e.stopPropagation()}
            sx={{
              borderRadius: 1,
              color: 'text.secondary',
              height: 34,
              width: 34,
              '&:hover': {
                bgcolor: alpha('#E2330A', 0.1),
                color: 'secondary.main',
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Progress + CTA anchored to the bottom edge so they line up across
          cards of the same grid row regardless of header height. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto', pt: 0.5 }}>
        <DictProgress total={dict.total} learned={dict.learned} height={7} />
        <Button
          fullWidth
          size={'small'}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 17 }} />}
        >
          {'Відкрити'}
        </Button>
      </Box>
    </Box>
  );
};

export default CustomDictionaryCard;
